import { useState, useRef } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { useTranslation } from 'react-i18next'
import { useCatalog, useShop } from '../shared/api/storefront'
import ProductCard from './components/ProductCard'
import CartBar from './components/CartBar'
import CartDrawer from './components/CartDrawer'

export default function CatalogPage() {
  const { t } = useTranslation()
  const { data: shop } = useShop()
  const { data: catalog, isLoading, isError } = useCatalog()
  const [cartOpen, setCartOpen] = useState(false)
  const currency = shop?.currency ?? 'INR'
  // Priceless shop (D-069): default true until the shop loads so a priced shop never flashes price-less.
  const showPrices = shop?.showPrices ?? true
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (isError || !catalog) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('catalog.loadError')}</Alert>
      </Container>
    )
  }
  if (catalog.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">{t('catalog.empty')}</Alert>
      </Container>
    )
  }

  const scrollTo = (id: string) =>
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <Container maxWidth="md" sx={{ py: 2, pb: 12 }}>
      {/* Category quick-nav pills (horizontal scroll → jump to a section). Only with >1 category. */}
      {catalog.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1.5,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {catalog.map((c) => (
            <Chip
              key={c.id}
              label={c.name}
              onClick={() => scrollTo(c.id)}
              variant="outlined"
              sx={{
                flexShrink: 0,
                bgcolor: 'background.paper',
                borderColor: 'secondary.main',
                color: 'text.primary',
                fontWeight: 700,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            />
          ))}
        </Box>
      )}

      <Stack spacing={4}>
        {catalog.map((category, categoryIndex) => (
          <Box
            key={category.id}
            ref={(el: HTMLDivElement | null) => {
              sectionRefs.current[category.id] = el
            }}
            sx={{ scrollMarginTop: 72 }}
          >
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="h6" color="primary.main">{category.name}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                {t('cart.item', { count: category.products.length })}
              </Typography>
            </Stack>
            {/* Horizontal rows on phones (xs); vertical-card grid on tablet/PC (sm+). */}
            <Box sx={{ display: 'grid', gap: { xs: 1.25, sm: 2 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
              {category.products.map((product, productIndex) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  currency={currency}
                  // First few cards are above the fold — everything below lazy-loads.
                  eager={categoryIndex === 0 && productIndex < 3}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>

      <CartBar currency={currency} showPrices={showPrices} onView={() => setCartOpen(true)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} currency={currency} showPrices={showPrices} />
    </Container>
  )
}
